// Two-axis empirical unit grade, from the precomputed rosterStats.json:
//
//   COLOR  = type tier — how good this *kind* of unit is, absolutely (its class/
//            type overall win rate → gold / blue / green / grey). A Floating Eye
//            is grey no matter how it's rolled; a Holy Dragon is gold.
//   LETTER = specimen — how good *this one* is for its type, relative to an
//            average unit of the type:
//              · monsters: their Brave/Faith vs that monster's B/F win curves
//                (the only thing that varies on a monster);
//              · humans: their skills + (with gameData) their post-gear stats and
//                Brave/Faith, scored by archetype (high PA helps melee, high MA/
//                faith helps casters, etc.).
//
// So an 80-Brave Floating Eye reads "grey A" — a great specimen of a weak type.
// Correlational, not a win probability — label it that way in the UI.

import { unitCombatStats } from './unitStats.js';
import { unitSwingDamage, swingBucket } from './swingDamage.js';

// Bucketers — MUST match tools/buildRosterStats.mjs so lookups hit the data.
export const sBucket = {
  PA: (v) => String(Math.round(v || 0)),
  MA: (v) => String(Math.round(v || 0)),
  Speed: (v) => String(Math.round(v || 0)),
  HP: (v) => { const d = Math.floor((v || 0) / 25) * 25; return d < 100 ? '<100' : `${d}-${d + 24}`; },
};
export const bfBucket = (v) => { v = v || 0; if (v < 40) return '<40'; if (v >= 85) return '85+'; const lo = Math.floor(v / 5) * 5; return `${lo}-${lo + 4}`; };

// Bucket → representative Brave/Faith value, for fitting a trend across a monster's
// bucketed B/F win curve.
const MID_BF = { '<40': 37, '40-44': 42, '45-49': 47, '50-54': 52, '55-59': 57, '60-64': 62, '65-69': 67, '70-74': 72, '75-79': 77, '80-84': 82, '85+': 88 };
// Sample-weighted linear fit of win rate on Brave/Faith over a monster's bucketed
// curve. Grading a monster off the FITTED trend at its EXACT Brave/Faith — instead
// of snapping to the raw bucket — removes the two artifacts that made monster grades
// jump on tiny B/F changes: (1) the cliffs at bucket boundaries (an 82-Brave Tiamat
// no longer grades a full letter below a 79-Brave one), and (2) the small-sample
// noise at the extremes (the >=85 bucket is absent for every monster and the <40 /
// 80-84 bins are thin, so a single noisy bin could crater a strong monster). The fit
// down-weights those thin bins and extrapolates through the missing 85+ instead of
// silently dropping the signal. Returns { predict, lo, hi } or null (no data).
export function bfFit(curve) {
  if (!curve || !curve.length) return null;
  const pts = curve.map((b) => ({ x: MID_BF[b.bucket], y: b.winRate, w: b.played || 1 })).filter((p) => p.x != null);
  if (!pts.length) return null;
  let sw = 0, sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (const p of pts) { sw += p.w; sx += p.w * p.x; sy += p.w * p.y; sxx += p.w * p.x * p.x; sxy += p.w * p.x * p.y; }
  const mx = sx / sw, my = sy / sw, denom = sxx - sw * mx * mx;
  const slope = denom > 1e-9 ? (sxy - sw * mx * my) / denom : 0; // one distinct bucket → flat
  const b0 = my - slope * mx;
  const xs = pts.map((p) => p.x);
  const clamp = (w) => Math.max(0, Math.min(100, w));
  return { predict: (v) => clamp(b0 + slope * (v || 0)), lo: Math.min(...xs), hi: Math.max(...xs) };
}

// Archetype from class base stats (PA vs MA); monsters are their own bucket.
export function archetypeOf(classBase, isMonster) {
  if (isMonster) return 'monster';
  if (!classBase || (!classBase.pa && !classBase.ma)) return 'hybrid';
  if (classBase.ma > classBase.pa + 1) return 'caster';
  if (classBase.pa > classBase.ma + 1) return 'melee';
  return 'hybrid';
}

const TIERS = [[55, 'gold'], [51, 'blue'], [47, 'green'], [-1, 'grey']];
const colorFor = (wr) => (TIERS.find(([t]) => wr >= t) || [-1, 'grey'])[1];

const SKILL_W = 0.5, STAT_W = 0.5, BF_W = 0.3;
// Swing (basic-attack burst) weight. It OVERLAPS the StatPA/StatMA buckets +
// the weapon/Dual-Wield/Doublehand dims it's derived from, but the signal is
// marginal-to-PA (a +12–18pt separator at fixed PA), so it earns a full stat-
// tier weight rather than a token nudge. Tunable middle ground: raise it to lean
// harder into burst, drop toward 0 to mute it.
const SWING_W = 0.5;

// Skill pairs worth more together than apart, added to a human's lift as one
// part. Only pairs with a mechanical reason AND a measured edge that held up in
// two separate half-years: mining every Support/Reaction/Move pair turned up
// noise (the two half-years' pair edges correlated at 0.05 over 400 pairs).
// `bonus` is in score points; a unit-score point is worth ~1.25 points of match
// win rate, so bonus ≈ measured edge / 1.25.
//   Mana Shield + Manafont: Mana Shield takes damage out of MP, Manafont refills
//   MP on every move. +3.3 points of match win rate beyond each skill alone
//   (+2.8 / +3.8 in the older / newer half of the year to 2026-10, ~1,700 units).
export const SYNERGIES = [
  { label: 'Mana Shield + Manafont', has: (u) => u.ReactionSkill === 'Mana Shield' && u.MoveSkill === 'Manafont', bonus: 2.5 },
];

// Compute the theoretical best and worst lift for a class+gender across the
// same dimensions the grade uses. Returns { min, max } — the floor/ceiling of
// what any unit of this type could score. Used to normalize the letter grade
// so each class's scale is relative to its own range, not a fixed threshold.
function liftRange(rs, cls, gender, typeWR, arch) {
  let minL = 0, maxL = 0;
  const isMonster = gender === 'Monster';
  const gKey = !isMonster && cls && gender ? cls + ' ' + gender : null;
  const RANGE_MIN_SAMPLE = 100; // ignore extreme outliers with tiny sample sizes
  // Skills: scan crossByClass for best/worst available WR per dim.
  if (!isMonster) {
    for (const dim of ['SupportSkill', 'ReactionSkill', 'ActionSkill', 'MoveSkill']) {
      const cb = rs && rs.crossByClass && rs.crossByClass[dim];
      if (!cb) continue;
      let best = typeWR, worst = typeWR;
      for (const skill in cb) {
        let wr = null, n = 0;
        if (gKey) { const h = cb[skill].find((x) => x.value === gKey); if (h) { wr = h.winRate; n = h.played; } }
        if (wr == null) { const h = cb[skill].find((x) => x.value === cls); if (h) { wr = h.winRate; n = h.played; } }
        if (wr != null && n >= RANGE_MIN_SAMPLE) { if (wr > best) best = wr; if (wr < worst) worst = wr; }
      }
      maxL += (best - typeWR) * SKILL_W;
      minL += (worst - typeWR) * SKILL_W;
    }
    for (const s of SYNERGIES) maxL += s.bonus;
    // Weapon: from flat dims (no cross-tab for weapons).
    const weapons = rs && rs.dims && rs.dims.Weapon;
    if (weapons && weapons.length) {
      const f = weapons.filter((w) => w.played >= RANGE_MIN_SAMPLE);
      if (f.length) { maxL += (Math.max(...f.map((w) => w.winRate)) - typeWR) * SKILL_W; minL += (Math.min(...f.map((w) => w.winRate)) - typeWR) * SKILL_W; }
    }
    // Stats + Brave/Faith (conditional by class+gender, falling back to archetype).
    const cgKey = !isMonster && cls && gender ? cls + ' ' + gender : null;
    if (arch || cgKey) {
      for (const dim of ['StatPA', 'StatMA', 'StatSpeed', 'StatHP']) {
        const c = rs && rs.conditional && rs.conditional[dim];
        const a = (cgKey && c && c[cgKey]) || (arch && c && c[arch]);
        if (!a || !a.length) continue;
        const f = a.filter((x) => x.played >= RANGE_MIN_SAMPLE);
        if (f.length) { maxL += (Math.max(...f.map((x) => x.winRate)) - typeWR) * STAT_W; minL += (Math.min(...f.map((x) => x.winRate)) - typeWR) * STAT_W; }
      }
      for (const dim of ['Brave', 'Faith']) {
        const c = rs && rs.conditional && rs.conditional[dim];
        const a = (cgKey && c && c[cgKey]) || (arch && c && c[arch]);
        if (!a || !a.length) continue;
        const f = a.filter((x) => x.played >= RANGE_MIN_SAMPLE);
        if (f.length) { maxL += (Math.max(...f.map((x) => x.winRate)) - typeWR) * BF_W; minL += (Math.min(...f.map((x) => x.winRate)) - typeWR) * BF_W; }
      }
      // Swing damage — conditional like the stat buckets, its own weight.
      {
        const c = rs && rs.conditional && rs.conditional.StatSwing;
        const a = (cgKey && c && c[cgKey]) || (arch && c && c[arch]);
        if (a && a.length) {
          const f = a.filter((x) => x.played >= RANGE_MIN_SAMPLE);
          if (f.length) { maxL += (Math.max(...f.map((x) => x.winRate)) - typeWR) * SWING_W; minL += (Math.min(...f.map((x) => x.winRate)) - typeWR) * SWING_W; }
        }
      }
    }
  } else {
    // Monsters: fitted Brave/Faith trends. The achievable lift extremes are the
    // fit evaluated at the ends of the observed B/F domain (the fit is linear, so
    // the extremes are the endpoints) — an ACHIEVABLE range (a unit really can roll
    // near those ends), matching the fitted lift the grade uses. A flat fit (B/F
    // doesn't move this monster) collapses the range → its specimens grade middling.
    const mb = rs && rs.monsterBF && rs.monsterBF[cls];
    if (mb) {
      for (const curve of [mb.brave, mb.faith]) {
        const fit = bfFit(curve);
        if (!fit) continue;
        const ends = [fit.predict(fit.lo), fit.predict(fit.hi)];
        const w = 0.5; // monsters split evenly across B/F
        maxL += (Math.max(...ends) - typeWR) * w;
        minL += (Math.min(...ends) - typeWR) * w;
      }
    }
  }
  return { min: +minL.toFixed(1), max: +maxL.toFixed(1) };
}

// Map a lift to a letter grade based on where it falls in the class's range.
// Zero lift (= average unit) maps to 0.5; positive lifts scale into [0.5, 1.0]
// using max, negative into [0.0, 0.5] using min — so the scale is symmetric
// around "average" even when the range is lopsided.
const LETTER_PCTS = [[0.9, 'A+'], [0.75, 'A'], [0.6, 'B'], [0.45, 'C+'], [0.3, 'C'], [0.18, 'D'], [0.08, 'E'], [0, 'F']];
const letterFor = (lift, range) => {
  if (!range || (range.max <= 0 && range.min >= 0)) return 'C';
  let pct;
  if (lift >= 0) { pct = range.max > 0 ? 0.5 + 0.5 * (lift / range.max) : 1; }
  else { pct = range.min < 0 ? 0.5 - 0.5 * (lift / range.min) : 0; }
  pct = Math.max(0, Math.min(1, pct));
  return (LETTER_PCTS.find(([t]) => pct >= t) || [0, 'F'])[1];
};

// PERCENTILE letter (humans). letterFor normalizes a unit's lift against the
// theoretical best/worst lift for its class — but that ceiling stacks the best
// win-rate option in EVERY dimension at once, which no real roll reaches (~13% of
// it, empirically), so every human collapsed to C+/C. Instead grade a human by
// where its lift ranks among the REALIZED lifts of real rolls of its own
// class+gender: gradeQuantiles.keys[cgKey] holds that lift distribution as the lift
// value at each LIFT_KNOTS percentile (emitted by tools/buildGradeQuantiles.mjs).
// A = "top ~11% roll for this class". Falls back to letterFor when the table is
// absent (partial build) or the unit was graded without gameData (skills-only lift
// is on a different scale than the with-gameData quantiles).
// Percentile positions the stored quantile knots correspond to. Denser at the tails
// so the rare-tail band cuts (A+ 98th, E 2nd) land on a knot and the tails fill to
// their intended width instead of getting washed out by wide-segment interpolation.
export const LIFT_KNOTS = [0, 1, 2, 3, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 97, 98, 99, 100];
// Percentile bands → letter. Bell-shaped: rare A+/F tails, a broad C+/C middle, but
// far more spread than the old envelope. Tune the cut-points to reshape the curve.
export const LETTER_BANDS = [[0.98, 'A+'], [0.89, 'A'], [0.72, 'B'], [0.50, 'C+'], [0.28, 'C'], [0.11, 'D'], [0.02, 'E'], [0, 'F']];
export const letterFromPct = (p) => (LETTER_BANDS.find(([t]) => p >= t) || [0, 'F'])[1];
// knots = ascending lift value at each LIFT_KNOTS percentile → percentile (0..1) of
// `lift`, linear-interpolated between the surrounding knots (clamped at the ends).
export function pctFromKnots(knots, lift) {
  if (!knots || !knots.length) return 0.5;
  if (lift <= knots[0]) return 0;
  if (lift >= knots[knots.length - 1]) return 1;
  let i = 1; while (i < knots.length && knots[i] < lift) i++;
  const lo = knots[i - 1], hi = knots[i];
  const frac = hi > lo ? (lift - lo) / (hi - lo) : 0;
  return (LIFT_KNOTS[i - 1] + frac * (LIFT_KNOTS[i] - LIFT_KNOTS[i - 1])) / 100;
}

const dimWR = (rs, dim, value) => {
  const a = rs && rs.dims && rs.dims[dim];
  if (!a || value == null || value === '') return null;
  const hit = a.find((x) => x.value === value);
  return hit ? hit.winRate : null;
};
// Conditional stat lookup: tries class+gender first (e.g. "Wizard Female"),
// falls back to archetype (e.g. "caster").
const condWR = (rs, trait, arch, value, clsGender) => {
  const c = rs && rs.conditional && rs.conditional[trait];
  if (!c || value == null) return null;
  if (clsGender) { const a = c[clsGender]; if (a) { const hit = a.find((x) => x.value === value); if (hit) return hit.winRate; } }
  const a = c[arch];
  if (!a) return null;
  const hit = a.find((x) => x.value === value);
  return hit ? hit.winRate : null;
};
const curveWR = (curve, b) => { const hit = curve && curve.find((x) => x.bucket === b); return hit ? hit.winRate : null; };
// Per-class win rate for a skill (crossByClass[dim][value] → find class+gender entry).
// Tries gender-specific ("Monk Male") first, falls back to gender-blind ("Monk"),
// then to the flat dimWR when per-class data is unavailable.
const crossWR = (rs, dim, value, cls, gender) => {
  const cross = rs && rs.crossByClass && rs.crossByClass[dim] && rs.crossByClass[dim][value];
  if (cross) {
    if (cls && gender && gender !== 'Monster') { const hit = cross.find((x) => x.value === cls + ' ' + gender); if (hit) return hit.winRate; }
    if (cls) { const hit = cross.find((x) => x.value === cls); if (hit) return hit.winRate; }
  }
  return dimWR(rs, dim, value);
};

// unit: a roster unit. gameData (optional): { charStats, itemschanged, notAbles }
// — pass it to enable the human stat / Brave-Faith enrichment (the card has all
// three as props). Without it, humans are graded on their skills alone.
export function unitGrade(unit, rosterStats, gameData) {
  const isMonster = (unit && unit.Gender) === 'Monster';
  // Gender-aware class win rate (Female Monk 47.9% vs Male Monk 53%) when
  // available; falls back to gender-blind Class rate for monsters / unknown.
  const genderedKey = !isMonster && unit && unit.Class && unit.Gender ? (unit.Class + ' ' + unit.Gender) : null;
  const typeWR = (genderedKey && dimWR(rosterStats, 'ClassGender', genderedKey)) ?? dimWR(rosterStats, 'Class', unit && unit.Class) ?? 50;
  const parts = [];
  let lift = 0;
  const add = (label, dim, winRate, weight) => { const c = +((winRate - typeWR) * weight).toFixed(1); lift += c; parts.push({ label, dim, winRate, contribution: c }); };

  if (isMonster) {
    // Monsters are identical but for Brave/Faith — score them on this type's own
    // Brave and Faith win curves (separately), relative to the type's average. The
    // score reads the sample-weighted FITTED trend at the unit's exact B/F (see
    // bfFit) rather than the raw 5-wide bucket, so the grade moves smoothly with B/F
    // instead of jumping at bucket boundaries or on a single thin extreme bin.
    const mb = rosterStats && rosterStats.monsterBF && rosterStats.monsterBF[unit.Class];
    if (mb) {
      const sigs = [['Brave', unit.Brave || 0, bfFit(mb.brave)], ['Faith', unit.Faith || 0, bfFit(mb.faith)]].filter((s) => s[2]);
      const w = sigs.length ? 1 / sigs.length : 0;
      for (const [dim, val, fit] of sigs) { const wr = +fit.predict(val).toFixed(1); const c = +((wr - typeWR) * w).toFixed(1); lift += c; parts.push({ label: `${dim} ${val}`, dim, winRate: wr, contribution: c }); }
    }
  } else {
    // Skills + WEAPON. The weapon's identity matters beyond its raw stats
    // (type / range / element), so it counts; the other gear slots are
    // intentionally omitted — their effect is already in the post-gear
    // PA/MA/Speed/HP buckets below, so scoring them too would double-count.
    // The Move skill counts too. It was left out until 2026-10-23, and graded
    // out of sample Move+3, Move+2 and Teleport 2 units kept winning ~2–3 points
    // more than their grade said.
    const cls = unit && unit.Class;
    for (const [dim, field] of [['SupportSkill', 'SupportSkill'], ['ReactionSkill', 'ReactionSkill'], ['ActionSkill', 'ActionSkill'], ['MoveSkill', 'MoveSkill'], ['Weapon', 'Mainhand']]) {
      const v = unit && unit[field];
      const wr = crossWR(rosterStats, dim, v, cls, unit.Gender);
      if (wr != null) add(v, dim, wr, SKILL_W);
    }
    // Dual Wield's 2nd weapon (via the support OR innate, e.g. Ninja) was
    // previously ignored — the grade only read Mainhand. Score the offhand
    // under the Weapon dim too, at half weight (a secondary swing). A shield or
    // relic in the offhand returns null here (it never appears as a Mainhand, so
    // it's absent from the Weapon dim), so non-dual-wielders self-skip.
    if (unit && unit.Offhand) {
      const wr = crossWR(rosterStats, 'Weapon', unit.Offhand, cls, unit.Gender);
      if (wr != null) add(unit.Offhand, 'Weapon', wr, SKILL_W * 0.5);
    }
    for (const s of SYNERGIES) {
      if (!s.has(unit)) continue;
      lift += s.bonus;
      parts.push({ label: s.label, dim: 'Combo', winRate: +(typeWR + s.bonus).toFixed(1), contribution: s.bonus });
    }
    const cs = gameData && gameData.charStats;
    if (cs) {
      const classBase2 = cs[(unit.Class || '').replace(/\s+/g, '') + (unit.Gender || '')];
      const arch2 = archetypeOf(classBase2, false);
      const cgKey = unit.Class && unit.Gender && unit.Gender !== 'Monster' ? unit.Class + ' ' + unit.Gender : null;
      const st = unitCombatStats(unit, cs, gameData.itemschanged, gameData.notAbles).stats;
      for (const [dim, val] of [['StatPA', sBucket.PA(st.pa)], ['StatMA', sBucket.MA(st.ma)], ['StatSpeed', sBucket.Speed(st.speed)], ['StatHP', sBucket.HP(st.hp)]]) {
        const wr = condWR(rosterStats, dim, arch2, val, cgKey);
        if (wr != null) add(`${dim.slice(4)} ${val}`, dim, wr, STAT_W);
      }
      for (const [dim, val] of [['Brave', bfBucket(unit.Brave)], ['Faith', bfBucket(unit.Faith)]]) {
        const wr = condWR(rosterStats, dim, arch2, val, cgKey);
        if (wr != null) add(`${dim} ${val}`, dim, wr, BF_W);
      }
      // Swing damage — the unit's basic-attack burst (mainhand, doubled for
      // Doublehand, + the Dual-Wield offhand). Bucketed win rate, conditional by
      // archetype/class. Captures the stat×weapon interaction the StatPA/StatMA
      // buckets and the per-weapon win rate each miss on their own.
      const sb = swingBucket(unitSwingDamage(unit, cs, gameData.itemschanged, gameData.notAbles));
      if (sb) { const wr = condWR(rosterStats, 'StatSwing', arch2, sb, cgKey); if (wr != null) add(`Swing ${sb}`, 'StatSwing', wr, SWING_W); }
    }
  }

  // Compute the theoretical best/worst lift for this class+gender so the letter
  // grade is relative to the class's own range, not a fixed threshold.
  const cs = gameData && gameData.charStats;
  const classBase = !isMonster && cs ? cs[(unit.Class || '').replace(/\s+/g, '') + (unit.Gender || '')] : null;
  const arch = classBase ? archetypeOf(classBase, false) : null;
  const range = liftRange(rosterStats, unit && unit.Class, unit && unit.Gender, typeWR, arch);

  // Human letter: prefer the realized-lift percentile (see LIFT_KNOTS) when the
  // precomputed distribution for this class+gender is available AND the unit was
  // graded with gameData (so the lift is on the same scale). Otherwise the range
  // fallback. Monsters always use the (achievable) range — their letters already
  // spread fine on the fitted B/F trend.
  const cgKey = !isMonster && unit && unit.Class && unit.Gender ? unit.Class + ' ' + unit.Gender : null;
  const gq = rosterStats && rosterStats.gradeQuantiles && rosterStats.gradeQuantiles.keys;
  const knots = cs && cgKey && gq && gq[cgKey];
  const percentile = knots ? pctFromKnots(knots, lift) : null;
  const grade = percentile != null ? letterFromPct(percentile) : letterFor(lift, range);

  return {
    typeWR: +typeWR.toFixed(1), color: colorFor(typeWR),
    specimenLift: +lift.toFixed(1), grade,
    score: +Math.max(0, Math.min(100, typeWR + lift)).toFixed(1),
    parts, range,
    ...(percentile != null ? { percentile: +percentile.toFixed(3) } : {}),
  };
}

// Team grade = mean of the units' absolute (expected-win-rate) scores, with a
// letter on that mean (teams cluster ~48-53, so the bands are tight) and a
// color tier from the same mean. A team-score point is worth ~5 points of match
// win rate, and 49.5 is about even. A+ starts at 55 (the top ~2% of teams, ~75%
// match wins on the newest 300 tournaments to 2026-10-23); at 54 it took in the
// top ~5%, and A then won less often than B+.
export const TEAM_LETTERS = [[55, 'A+'], [53, 'A'], [52, 'B+'], [51, 'B'], [50, 'C+'], [49, 'C'], [48, 'D'], [46, 'E'], [0, 'F']];
export const teamLetterFor = (s) => (TEAM_LETTERS.find(([t]) => s >= t) || [0, 'F'])[1];
export function teamGrade(units, rosterStats, gameData) {
  const unitGrades = (units || []).map((u) => ({ name: u.Name, cls: u.Class, ...unitGrade(u, rosterStats, gameData) }));
  const score = unitGrades.length ? +(unitGrades.reduce((s, g) => s + g.score, 0) / unitGrades.length).toFixed(1) : 50;
  return { score, color: colorFor(score), grade: teamLetterFor(score), units: unitGrades };
}

// Compact view of a grade's drivers: the n biggest positive and n biggest
// negative contributors (+ a count of the rest). For the decluttered hover.
export function topParts(parts, n = 2) {
  const nz = [...(parts || [])].filter((p) => p.contribution).sort((a, b) => b.contribution - a.contribution);
  const up = nz.filter((p) => p.contribution > 0).slice(0, n);
  const down = nz.filter((p) => p.contribution < 0).slice(-n).reverse();
  return { up, down, more: nz.length - up.length - down.length };
}
